import React, { useMemo, useState } from "react";
import { FiFilter, FiAlertCircle } from "react-icons/fi";
import useReadAllCharterRequests from "../lib/useReadAllCharterRequests";
import { calculateWorkflowStep } from "../../Utils/workflowStepCalculator";
import Skeleton from "react-loading-skeleton";
import "./ActiveVessels.css";

export default function ActiveVessels() {
  const { charterRequests, charterRequestsLoading, charterRequestsError } = useReadAllCharterRequests();

  // Map API step to 12-step display step
  const getDisplayStep = (apiStep) => {
    switch (apiStep) {
      case 1: return 1; // charterRequest (charterer)
      case 2: return 3; // charterResponse (vessel owner)
      case 3: return 4; // processCharterResponse (admin)
      case 4: return 5; // isCharterMovingForward (charterer)
      case 5: return 6; // processCharterMovingForward (admin)
      case 6: return 7; // selectMeetingDate (both)
      case 7: return 8; // meeting result saved (system)
      case 8: return 9; // signVesselNominationAgreement (both)
      case 9: return 10; // processVesselInspectionResult (admin)
      case 10: return 11; // signCharterPartyAgreement (both)
      case 11: return 12; // processCharterPartyAgreement (admin)
      default: return apiStep; // includes 12 -> 12
    }
  };

  const completedCharters = useMemo(() => {
    if (!charterRequests || charterRequests.length === 0) return [];
    return charterRequests.filter((req) => {
      const apiStep = req.step !== undefined ? req.step : calculateWorkflowStep(req);
      const displayStep = getDisplayStep(apiStep);
      return displayStep >= 12; // Completed
    });
  }, [charterRequests]);

  // Transform request into card-friendly data
  const cardData = useMemo(() => {
    return completedCharters.map((req) => {
      const start = req.startDate ? new Date(req.startDate) : null;
      const end = start ? new Date(start.getTime() + (Number(req.duration || 0) * 24 * 60 * 60 * 1000)) : null;
      const origin = req.origin?.coordinates ? req.origin.coordinates.join(", ") : null;
      const destination = req.destination?.coordinates ? req.destination.coordinates.join(", ") : null;
      const route = origin && destination ? `${origin} → ${destination}` : "N/A";
      const pricePerDay = req.finalProposedPrice || req.pricing;
      const totalCharterPrice = pricePerDay && req.duration ? pricePerDay * req.duration : null;
      const securityPrice = req.canProvideSecurity?.pricing || null;
      const grandTotal = totalCharterPrice && securityPrice ? totalCharterPrice + securityPrice : totalCharterPrice;

      return {
        id: req._id,
        vesselName: `Charter #${req._id.slice(-8)}`,
        vesselType: "Completed Charter",
        image: "https://res.cloudinary.com/du44pwjoi/image/upload/v1742471097/csv_klkqpb.png",
        specifications: {
          passengers: `${req.numberOfPassengers || 0} Passengers`,
          security: req.security ? "Security Required" : "No Security",
          originalPrice: req.pricing ? `Original: $${req.pricing.toLocaleString()}/Day` : undefined,
          finalPrice: req.finalProposedPrice ? `Final: $${req.finalProposedPrice.toLocaleString()}/Day` : undefined,
          securityPricing: securityPrice ? `Security: $${securityPrice.toLocaleString()}` : undefined,
          charterTotal: totalCharterPrice ? `Charter Total: $${totalCharterPrice.toLocaleString()}` : undefined,
          securityTotal: securityPrice ? `Security Cost: $${securityPrice.toLocaleString()}` : undefined,
          grandTotal: grandTotal ? `Grand Total: $${grandTotal.toLocaleString()}` : undefined,
        },
        route,
        duration: `${req.duration || 0} Days`,
        price: pricePerDay ? `$${pricePerDay.toLocaleString()}/Day` : "N/A",
        startDate: start ? start.toLocaleDateString() : "N/A",
        endDate: end ? end.toLocaleDateString() : "N/A",
        status: "Completed",
        remainingDays: 0,
        fuelConsumption: "N/A",
        captain: "N/A",
        maintenanceDate: "N/A",
        operationalStatus: "Completed",
        lastInspection: req.vesselInspected?.result || "N/A",
      };
    });
  }, [completedCharters]);

  // State for expanded vessel details
  const [expandedId, setExpandedId] = useState(null);
  const [activeFilter, setActiveFilter] = useState("All");
  const [sortBy, setSortBy] = useState("remainingDays");
  const [sortOrder, setSortOrder] = useState("asc");

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  // Filter types based on unique vessel types
  const vesselTypes = useMemo(() => ["All", ...new Set(cardData.map(vessel => vessel.vesselType))], [cardData]);

  // Sort and filter data
  const filteredAndSortedData = useMemo(() => {
    const list = cardData
      .filter(vessel => activeFilter === "All" || vessel.vesselType === activeFilter)
      .sort((a, b) => {
        const sortValue = sortOrder === "asc" ? 1 : -1;
        if (sortBy === "remainingDays") {
          return (a.remainingDays - b.remainingDays) * sortValue;
        }
        if (sortBy === "price") {
          const aPrice = parseInt(String(a.price).replace(/\D/g, "")) || 0;
          const bPrice = parseInt(String(b.price).replace(/\D/g, "")) || 0;
          return (aPrice - bPrice) * sortValue;
        }
        return 0;
      });
    return list;
  }, [cardData, activeFilter, sortBy, sortOrder]);

  if (charterRequestsLoading) {
    return (
      <div className="active-vessels-container">
        <div className="active-vessels-header">
          <h2 className="active-vessels-title">Active Vessels</h2>
        </div>
        <div className="active-vessels-list">
          {[1,2,3].map(i => (
            <div key={i} className="active-vessel-card">
              <Skeleton height={180} style={{ marginBottom: 16 }} />
              <Skeleton height={16} count={4} style={{ marginBottom: 8 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (charterRequestsError) {
    return (
      <div className="active-vessels-container">
        <div className="active-vessels-header">
          <h2 className="active-vessels-title">Active Vessels</h2>
        </div>
        <div style={{ 
          display: "flex", 
          flexDirection: "column", 
          alignItems: "center", 
          justifyContent: "center", 
          padding: "40px 20px",
          textAlign: "center"
        }}>
          <FiAlertCircle size={48} color="#f59e0b" style={{ marginBottom: "12px" }} />
          <p style={{ color: "#374151", fontWeight: "600", fontSize: "18px", margin: "0 0 4px 0" }}>Pending Approval</p>
          <p style={{ color: "#6b7280", fontSize: "14px", margin: "0" }}>Your active vessels are being reviewed.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="active-vessels-container">
      <div className="active-vessels-header">
        <h2 className="active-vessels-title">Active Vessels ({filteredAndSortedData.length})</h2>
        
        <div className="active-vessels-filters">
          <div className="filters-section">
            <div className="filter-label">
              <FiFilter />
              <span>Filter by:</span>
            </div>
            
            <div className="vessel-type-filters">
              {vesselTypes.map(type => (
                <button
                  key={type}
                  className={`vessel-type-button ${activeFilter === type ? 'active' : ''}`}
                  onClick={() => setActiveFilter(type)}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
          
          <div className="sort-controls">
            <select 
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="remainingDays">Sort by Remaining Days</option>
              <option value="price">Sort by Price</option>
            </select>
            <button 
              className="sort-order-button"
              onClick={() => setSortOrder(order => order === "asc" ? "desc" : "asc")}
            >
              {sortOrder === "asc" ? "↑" : "↓"}
            </button>
          </div>
        </div>
      </div>

      <div className="active-vessels-list">
        {filteredAndSortedData.length === 0 ? (
          <div className="no-charters-message">No completed charters yet.</div>
        ) : (
          filteredAndSortedData.map(charter => (
            <div className={`active-vessel-card ${expandedId === charter.id ? 'expanded' : ''}`} key={charter.id}>
              <div className="vessel-image-section">
                <img src={charter.image} alt={charter.vesselName} />
                <div className="vessel-status-overlay">
                  <span className="status-badge completed">Completed</span>
                  <span className="days-remaining">0 days left</span>
                </div>
              </div>
              
              <div className="vessel-content">
                <div className="vessel-header">
                  <div className="vessel-title-section">
                    <h3 className="vessel-name">{charter.vesselName}</h3>
                    <span className="vessel-type">{charter.vesselType}</span>
                  </div>
                  <div className="vessel-pricing">
                    <div className="vessel-price">{charter.price}</div>
                    <span className="price-period">({charter.duration})</span>
                  </div>
                </div>
                
                <div className="vessel-details">
                  <div className="specifications-section">
                    <h4 className="section-title">Specifications</h4>
                    <div className="specs-list">
                      {Object.values(charter.specifications).filter(Boolean).map((spec, index) => (
                        <span key={index} className="spec-item">{spec}</span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="booking-details-section">
                    <div className="detail-row">
                      <span className="detail-label">Route:</span>
                      <span className="detail-value">{charter.route}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Duration:</span>
                      <span className="detail-value">{charter.duration}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Completed On:</span>
                      <span className="detail-value highlight">{charter.endDate}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Status:</span>
                      <span className="status-badge completed">Completed</span>
                    </div>
                  </div>
                </div>
                
                {expandedId === charter.id && (
                  <div className="expanded-details">
                    {(charter.specifications.originalPrice || charter.specifications.finalPrice || charter.specifications.securityPricing || charter.specifications.charterTotal || charter.specifications.securityTotal || charter.specifications.grandTotal) && (
                      <div className="pricing-details-section">
                        <h4 className="section-title">Pricing Information</h4>
                        <div className="details-grid">
                          {charter.specifications.originalPrice && (
                            <div className="detail-item">
                              <span className="detail-label">Original Price:</span>
                              <span className="detail-value">{charter.specifications.originalPrice}</span>
                            </div>
                          )}
                          {charter.specifications.finalPrice && (
                            <div className="detail-item">
                              <span className="detail-label">Final Agreed Price:</span>
                              <span className="detail-value highlight">{charter.specifications.finalPrice}</span>
                            </div>
                          )}
                          {charter.specifications.securityPricing && (
                            <div className="detail-item">
                              <span className="detail-label">Security Pricing:</span>
                              <span className="detail-value">{charter.specifications.securityPricing}</span>
                            </div>
                          )}
                          {charter.specifications.charterTotal && (
                            <div className="detail-item">
                              <span className="detail-label">Charter Total:</span>
                              <span className="detail-value">{charter.specifications.charterTotal}</span>
                            </div>
                          )}
                          {charter.specifications.securityTotal && (
                            <div className="detail-item">
                              <span className="detail-label">Security Cost:</span>
                              <span className="detail-value">{charter.specifications.securityTotal}</span>
                            </div>
                          )}
                          {charter.specifications.grandTotal && (
                            <div className="detail-item">
                              <span className="detail-label">Grand Total:</span>
                              <span className="detail-value highlight">{charter.specifications.grandTotal}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="operation-details-section">
                      <h4 className="section-title">Operation Details</h4>
                      <div className="details-grid">
                        <div className="detail-item">
                          <span className="detail-label">Start Date:</span>
                          <span className="detail-value">{charter.startDate}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">End Date:</span>
                          <span className="detail-value">{charter.endDate}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Fuel Usage:</span>
                          <span className="detail-value">{charter.fuelConsumption}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Captain:</span>
                          <span className="detail-value">{charter.captain}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Next Maintenance:</span>
                          <span className="detail-value">{charter.maintenanceDate}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Inspection Result:</span>
                          <span className="detail-value">{charter.lastInspection}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="vessel-actions">
                  <button className="toggle-details-btn" onClick={() => toggleExpand(charter.id)}>
                    {expandedId === charter.id ? 'Hide Details' : 'View Details'}
                  </button>
                  <button className="action-btn disabled" disabled>Manage Charter</button>
                  <button className="action-btn disabled" disabled>Extend Charter</button>
                  <button className="action-btn disabled" disabled>Log Issue</button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
} 